import type paper from "paper";
import type { Project } from "./Project";
import type { EditorContext } from "../tools/EditorContext";
import {
  addPieceLayoutDelta,
  translatePiece,
  mirrorPiece,
  rotatePiece,
} from "./pieceTransform";
import type { ConstructionTransformOp } from "./constructionGroupActions";
import {
  computeArrangeDeltas,
  type ArrangeItem,
  type ArrangeOp,
} from "./arrange";

/**
 * Model ops for the Layout tool (#739) — arranging leather **parts** for
 * printing. Parts only; construction lines are never touched (that path stays in
 * `arrangeConstructionSelection`). Reuses the model-agnostic
 * `computeArrangeDeltas` engine for align/distribute and a fresh AABB de-overlap
 * for Auto-layout.
 */

/** Gap (project mm) kept between parts by Auto-layout's de-overlap. */
export const AUTO_LAYOUT_GAP_MM = 5;

/**
 * Parts that can actually be moved right now. A **linked** part is locked in the
 * Assembled cut-layout view — its `layout` offset only renders in Spread, so
 * moving it in Assembled would snap back on commit — so drop it there (mirrors
 * SelectTool). In Spread view (and whenever nothing is linked) every id passes.
 */
export function movablePieceIds(
  ctx: EditorContext,
  ids: ReadonlyArray<string>,
): string[] {
  if (ctx.scene.getPartLayoutView() !== "assembled") return [...ids];
  const linked = new Set(
    ctx.history.state.pieces
      .filter((p) => p.constructionOutline)
      .map((p) => p.id),
  );
  return ids.filter((id) => !linked.has(id));
}

/**
 * Move one part by `(dx, dy)`. A **linked** part (with a `constructionOutline`)
 * can't have its geometry rewritten — the construction re-derive would clobber
 * it — so the move accumulates into its cut-layout offset (`layout`), applied at
 * render/export time. An **unlinked** part translates its geometry directly.
 */
export function movePieceInProject(
  proj: Project,
  pieceId: string,
  dx: number,
  dy: number,
  scope: paper.PaperScope,
): Project {
  return {
    ...proj,
    pieces: proj.pieces.map((p) => {
      if (p.id !== pieceId) return p;
      return p.constructionOutline
        ? addPieceLayoutDelta(p, dx, dy)
        : translatePiece(p, dx, dy, scope);
    }),
  };
}

/**
 * Align / distribute the selected parts, mirroring `arrangeConstructionSelection`
 * but for pieces: gather each selected part's rendered bounds, run
 * `computeArrangeDeltas`, and commit the non-zero moves in one undo step.
 * Returns false (no-op) when fewer than 2 parts are selected or nothing moves.
 */
export function arrangePieceSelection(
  ctx: EditorContext,
  op: ArrangeOp,
): boolean {
  const sel = ctx.scene.getSelection();
  const ids = movablePieceIds(
    ctx,
    sel.pieceIds ?? (sel.pieceId ? [sel.pieceId] : []),
  );
  if (ids.length < 2) return false;

  const items: ArrangeItem[] = [];
  for (const id of ids) {
    const b = ctx.scene.getPieceBounds(id);
    if (b) {
      items.push({
        id,
        bounds: { x: b.x, y: b.y, width: b.width, height: b.height },
      });
    }
  }
  if (items.length < 2) return false;

  // #872: align / centre anchor on the FIRST-selected part — `pieceIds`
  // preserves click order (shift-click appends), and `items` follows it, so
  // items[0] is the earliest-selected movable part. It stays put and the
  // others come to its edges / centre lines.
  const moves = computeArrangeDeltas(items, op, items[0]!.id).filter(
    (d) => d.dx !== 0 || d.dy !== 0,
  );
  if (moves.length === 0) return false;

  const scope = ctx.scene.getPaperScope();
  ctx.commit(`Arrange parts (${op})`, (proj) => {
    let next = proj;
    for (const d of moves)
      next = movePieceInProject(next, d.id, d.dx, d.dy, scope);
    return next;
  });
  return true;
}

/**
 * Parts whose geometry can be flipped / rotated in place: **unlinked** parts
 * only. A linked part's `shapePathData` is re-derived from its construction
 * geometry on every commit, so a baked flip/rotate would be clobbered — and
 * `layout` carries only a translation, not a reflection/rotation. To mirror a
 * linked part, mirror its source construction instead. Unlike `movablePieceIds`
 * (which drops linked parts only in the Assembled view), this drops them in
 * every view.
 */
export function transformablePieceIds(
  ctx: EditorContext,
  ids: ReadonlyArray<string>,
): string[] {
  const linked = new Set(
    ctx.history.state.pieces
      .filter((p) => p.constructionOutline)
      .map((p) => p.id),
  );
  return ids.filter((id) => !linked.has(id));
}

/**
 * Flip / rotate the selected parts about the centre of their combined bounds —
 * mirroring `transformConstructionSelection` but for pieces (#795). Each part's
 * shape, holes, cutouts, stitch lines, and decorations transform together. A
 * single part flips / rotates about its own centre; a multi-part selection
 * transforms as a group (so positions mirror / rotate too). Linked parts are
 * skipped (see `transformablePieceIds`). Returns false when nothing is
 * transformable. y-down project space: a positive Paper rotation turns
 * clockwise, so rotate-right is +90 and rotate-left is -90.
 */
export function transformPieceSelection(
  ctx: EditorContext,
  op: ConstructionTransformOp,
): boolean {
  const sel = ctx.scene.getSelection();
  const ids = transformablePieceIds(
    ctx,
    sel.pieceIds ?? (sel.pieceId ? [sel.pieceId] : []),
  );
  if (ids.length === 0) return false;

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let found = false;
  for (const id of ids) {
    const b = ctx.scene.getPieceBounds(id);
    if (!b) continue;
    minX = Math.min(minX, b.x);
    minY = Math.min(minY, b.y);
    maxX = Math.max(maxX, b.x + b.width);
    maxY = Math.max(maxY, b.y + b.height);
    found = true;
  }
  if (!found) return false;
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;

  const scope = ctx.scene.getPaperScope();
  const idSet = new Set(ids);
  ctx.commit(`Transform parts (${op})`, (proj) => ({
    ...proj,
    pieces: proj.pieces.map((p) => {
      if (!idSet.has(p.id)) return p;
      switch (op) {
        case "rotate-right":
          return rotatePiece(p, 90, cx, cy, scope);
        case "rotate-left":
          return rotatePiece(p, -90, cx, cy, scope);
        case "flip-horizontal":
          return mirrorPiece(p, "horizontal", cx, cy, scope);
        case "flip-vertical":
          return mirrorPiece(p, "vertical", cx, cy, scope);
      }
    }),
  }));
  return true;
}

export interface LayoutBox {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Pure AABB de-overlap: given axis-aligned boxes, return the net `{dx, dy}` per
 * id that separates every pair so no two overlap (kept at least `gap` apart),
 * moving parts apart along their axis of least penetration and splitting the
 * push between the two so the arrangement stays roughly centred. Paper-free and
 * deterministic (fixed iteration order) so it's unit-testable.
 */
export function deOverlapBoxes(
  boxes: ReadonlyArray<LayoutBox>,
  gap: number,
): Map<string, { dx: number; dy: number }> {
  // Mutable working copy of each box's top-left; deltas are measured against the
  // originals at the end.
  const cur = boxes.map((b) => ({ id: b.id, x: b.x, y: b.y, w: b.w, h: b.h }));
  const n = cur.length;
  const MAX_ITERS = 400;
  for (let iter = 0; iter < MAX_ITERS; iter++) {
    let any = false;
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const a = cur[i]!;
        const b = cur[j]!;
        // Overlap (plus the desired gap) on each axis. Positive on both axes
        // means the pair is closer than `gap` and must be pushed apart.
        const ox = Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x) + gap;
        const oy = Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y) + gap;
        if (ox <= 0 || oy <= 0) continue;
        any = true;
        if (ox < oy) {
          const push = ox / 2;
          const dir = a.x + a.w / 2 <= b.x + b.w / 2 ? 1 : -1;
          a.x -= dir * push;
          b.x += dir * push;
        } else {
          const push = oy / 2;
          const dir = a.y + a.h / 2 <= b.y + b.h / 2 ? 1 : -1;
          a.y -= dir * push;
          b.y += dir * push;
        }
      }
    }
    if (!any) break;
  }
  const deltas = new Map<string, { dx: number; dy: number }>();
  for (let i = 0; i < n; i++) {
    deltas.set(cur[i]!.id, {
      dx: cur[i]!.x - boxes[i]!.x,
      dy: cur[i]!.y - boxes[i]!.y,
    });
  }
  return deltas;
}

/**
 * Auto-layout: spread overlapping parts apart until none overlap (#739). Targets
 * the selected parts when ≥2 are selected, else every part. Commits the net
 * per-part move in one undo step. Returns false when there's nothing to do.
 */
export function autoLayoutPieces(ctx: EditorContext): boolean {
  const sel = ctx.scene.getSelection();
  const allIds = ctx.history.state.pieces.map((p) => p.id);
  const targetIds = movablePieceIds(
    ctx,
    sel.pieceIds && sel.pieceIds.length >= 2 ? sel.pieceIds : allIds,
  );
  if (targetIds.length < 2) return false;

  const boxes: LayoutBox[] = [];
  for (const id of targetIds) {
    const b = ctx.scene.getPieceBounds(id);
    if (b) boxes.push({ id, x: b.x, y: b.y, w: b.width, h: b.height });
  }
  if (boxes.length < 2) return false;

  const deltas = deOverlapBoxes(boxes, AUTO_LAYOUT_GAP_MM);
  const moves = [...deltas.entries()].filter(
    ([, d]) => Math.abs(d.dx) > 1e-6 || Math.abs(d.dy) > 1e-6,
  );
  if (moves.length === 0) return false;

  const scope = ctx.scene.getPaperScope();
  ctx.commit("Auto-layout parts", (proj) => {
    let next = proj;
    for (const [id, d] of moves)
      next = movePieceInProject(next, id, d.dx, d.dy, scope);
    return next;
  });
  return true;
}
